import React, {useCallback, useEffect, useMemo, useState} from "react";
import Paper from "@mui/material/Paper";
import ImportingBottomActionBar from "./components/bottom-action-bar";
import ImportingViewStepperSection from "./components/stepper";
import RouteLeavingGuard from "../../../../../components/base/route-leaving-guard";
import Button from "../../../../../components/base/button/button";
import {useConfigureNotebook} from "../../../../../hooks/use-notebook-config";
import {useHistory} from "react-router-dom";
import ToastService from "../../../../../../core/services/toast";
import Texts from "../../../../../../core/models/static/texts/texts";
import SelectImportDataSourceSection from "./sections/select-data-source";
import ReviewImportingSection from "./sections/review-section";
import ImportingSection from "./sections/importing-section";
import useBehavioralAnalyticsTool from "../../../../../hooks/use-behavioural-analytics-tool";
import useImportStaticData from "./use-import-static-data";
import {Alert, Box, Typography} from "@mui/material";
import {useTranslation} from "react-i18next";
import {useSelectedDimensionMetrics} from "../../../../../components/app-specific/manage-product-dimensions/unit-management";
import useSavedComponentsState from "../../../../../hooks/use-saved-components-state";
import useIsMounted from "../../../../../hooks/use-is-mounted";
import {ImportingDataTypes, ImportingProcessStatus, ImportingSteps} from "./types";
import {BaseImportTransformer, correctiveInteractionRegistry, transformerRegistry} from "./engine";
import {DEFAULT_PRODUCT_CODE_SPLITTER} from "../../../../../components/app-specific/product-code-splitter";
import {resolveImportIndustryId} from "./engine/shared/import-industry-context";
import {industryHasUnsupportedMultiselectPredefined} from "./engine/shared/technical-import-helpers";
import {toSkippedFieldsByRow} from "./engine/preventative-interactions/shared";
import {useBizFormApiResolver} from "../../../../../hooks/use-biz-form-api-resolver";

/**
 * Builds the imported file data structure from the parsed rows, fields, and column mapping.
 * @param parsedRows
 * @param fields
 * @param columnMapping
 * @returns {{}[]}
 */
const buildImportedFileFromParsedRows = (parsedRows, fields, columnMapping) => {
    const mappedFieldNames = new Set(Object.keys(columnMapping ?? {}));
    return (parsedRows ?? []).map((parsedRow) => {
        const importedRow = {};
        for (const field of fields ?? []) {
            if (mappedFieldNames.has(field.name)) {
                importedRow[field.name] = parsedRow[field.name];
            }
        }
        return importedRow;
    });
};

const buildMappingFieldsForSession = (fields, columnMapping) => (fields ?? []).filter((field) => columnMapping?.[field.name] != null);


export const initialImportDataViewState = {
    // Current step in the import process
    step: ImportingSteps.selectDataSource,
    // Selected type of data to import
    selectedDataType: null,
    // Current field mappings
    columnMapping: null,
    // Transformed data ready for API
    apiRequests: null,
    // imported file data
    importedFile: null,
    // to be used as initial rows values in the ImportCsvFile's mapper component
    parsedRows: null,
    // to be used as initial columns values in the ImportCsvFile mapper component
    parsedColumns: null,
    originalApiRequests: [],
    // Preventative issues detected during transformation
    preventativeIssues: [],
    // Fields defined for the current data type (for i18n lookup)
    mappingFields: [],
    propertyOptionsByPropertyId: {},
    /** CSV row indices the user chose to remove in preventative correction */
    userSkippedRowIndices: [],
    /** Per-row skipped import field keys from preventative correction */
    userSkippedFieldCells: [],
    /** Row indices with no importable data after field skips */
    obsoleteRowIndices: [],
    /** Delimiter for splitting codes within a group column cell */
    codeImportSplitter: DEFAULT_PRODUCT_CODE_SPLITTER,
    /** Delimiter for splitting serial numbers in set inventory availability import */
    serialImportSplitter: ',',
    /** Selected industry for industry-scoped import data sources */
    importIndustryId: null,
};

const ImportDataView = () => {
    const history = useHistory();
    const {t} = useTranslation('views', {keyPrefix: 'app-specific.settingsImport'});
    const [processStatus, setProcessStats] = useState(ImportingProcessStatus.loading);
    const [updatingStep, setUpdatingStep] = useState(false);
    const {configureNotebookTags} = useConfigureNotebook('settings-import-view');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();

    const [state, setState] = useState(initialImportDataViewState);

    // ── Interactive correction state ───────────────────────────────────────────
    const [interactiveCorrectionEnabled, setInteractiveCorrectionEnabled] = useState(false);
    const isMounted = useIsMounted();

    const {highestVersion: getCachedCorrectionEnabled, save: saveCachedCorrectionEnabled} = useSavedComponentsState({
        name: "settings-import-interactive-correction",
        version: "1.0.0"
    });

    /**
     * Retrieves the cached interactive correction-enabled state from the local storage
     */
    useEffect(() => {
        void getCachedCorrectionEnabled().then((cached) => {
            if (!isMounted())
                return;
            if (cached?.data !== undefined) {
                setInteractiveCorrectionEnabled(Boolean(cached.data));
                window.partner = {
                    ...(window.partner ?? {}),
                    importingInteractiveCorrectionEnabled: Boolean(cached.data)
                }
            }
        });
    }, [getCachedCorrectionEnabled, isMounted]);

    /**
     * Handles changes to the interactive correction-enabled state
     * @type {(function(*): void)|*}
     */
    const handleInteractiveCorrectionEnabledChange = useCallback((enabled) => {
        setInteractiveCorrectionEnabled(enabled);
        void saveCachedCorrectionEnabled(enabled);
        window.partner = {
            ...(window.partner ?? {}),
            importingInteractiveCorrectionEnabled: enabled,
        }
    }, [saveCachedCorrectionEnabled]);

    /** Stable updater so useInteractiveCorrection can mutate remaining apiRequests */
    const setApiRequests = useCallback((updater) => {
        setState((prev) => ({
            ...prev,
            apiRequests: typeof updater === 'function' ? updater(prev.apiRequests) : updater,
        }));
    }, []);

    const formsApi = useBizFormApiResolver((t) => t.operatorApp);

    const {
        loading: staticDataLoading,
        allPriceLevels,
        allIndustries,
        allWarehouses,
        approvedPartners,
        allCategories,
        technicalPropertiesOfIndustry,
        allForms,
        allProductRelationTypes,
        allPartCodeCategories,
    } = useImportStaticData(state.selectedDataType, formsApi, state.importIndustryId);
    const {transformLengthValue, transformWeightValue} = useSelectedDimensionMetrics();

    const correctiveInteractions = useMemo(() => {
        if (!state.selectedDataType?.id) {
            return [];
        }
        return correctiveInteractionRegistry[state.selectedDataType.id] ?? [];
    }, [state.selectedDataType?.id]);

    /** Preloaded data passed directly into getFieldOptions (no context required) */
    const showMultiselectTechnicalImportWarning = useMemo(() => {
        const sourceId = state.selectedDataType?.id;
        if (sourceId !== ImportingDataTypes.partTechnicalInformation) {
            return false;
        }
        const industryId = resolveImportIndustryId(sourceId, state.importIndustryId);
        const properties = technicalPropertiesOfIndustry[industryId] ?? [];
        return industryHasUnsupportedMultiselectPredefined(properties);
    }, [state.selectedDataType?.id, state.importIndustryId, technicalPropertiesOfIndustry]);

    const importIndustryId = useMemo(
        () => resolveImportIndustryId(state.selectedDataType?.id, state.importIndustryId),
        [state.selectedDataType?.id, state.importIndustryId],
    );

    const staticDataContext = useMemo(() => ({
        allCategories,
        allIndustries,
        industryId: importIndustryId,
        allWarehouses,
        allPriceLevels,
        approvedPartners,
        technicalPropertiesOfIndustry,
        propertyOptionsByPropertyId: state.propertyOptionsByPropertyId ?? {},
        mappingFields: state.mappingFields ?? [],
        formsApi,
        allForms,
        allProductRelationTypes,
        allPartCodeCategories,
        codeImportSplitter: state.codeImportSplitter,
        serialImportSplitter: state.serialImportSplitter,
    }), [allCategories, allIndustries, importIndustryId, allWarehouses, allPriceLevels, approvedPartners, technicalPropertiesOfIndustry, state.propertyOptionsByPropertyId, state.mappingFields, formsApi, allForms, allProductRelationTypes, allPartCodeCategories, state.codeImportSplitter, state.serialImportSplitter]);

    /**
     * As soon as the component is mounted:
     * - Configures the notebook tags for the view
     * - Configures the behavioral analytics tool for the view
     */
    useEffect(() => {
        configureNotebookTags((noteTags) => noteTags.settingsImport);
        configureBehavioralTool((tags) => tags.settingsImport);
    }, [configureBehavioralTool, configureNotebookTags]);

    /**
     * Updates the current step in the import process
     * Ensures state consistency when navigating between steps
     *
     * @param {ImportingSteps} step - The new step to set
     */
    const setStep = useCallback((step) => setState(prevState => ({...prevState, step: step})), []);

    /**
     * Resets the import process to initial state
     * Used when starting a new import after completion
     * Clears all previous import data and mappings
     */
    const onNewProcessClicked = useCallback(() => {
        setState(initialImportDataViewState);
        setProcessStats(ImportingProcessStatus.loading);
        // clears the cache.
        BaseImportTransformer.sharedImportTransformContext.clearCache();
    }, []);

    /**
     * Clears the shared import transform context cache on component-mount
     */
    React.useEffect(() => {
        BaseImportTransformer.sharedImportTransformContext.clearCache();
    }, [])

    /**
     * Transforms imported data based on selected data type
     * Handles different transformations for various data types
     * Includes error handling and user feedback
     *
     * @param {Object} parsedRows - The data to transform
     * @returns {Promise<Object|null>} Transformed data or null if error
     */
    const transformImportedData = useCallback(async (
        parsedRows,
        isFinalRun = false,
        skippedFieldCells = null,
        mappingFieldsOverride = null,
    ) => {
        if (!state.selectedDataType?.id) return null;

        const mappingFields = mappingFieldsOverride ?? state.mappingFields ?? [];
        const cellsToApply = skippedFieldCells ?? state.userSkippedFieldCells ?? [];
        BaseImportTransformer.sharedImportTransformContext.setSkippedFieldsByRow(
            toSkippedFieldsByRow(cellsToApply),
        );

        try {
            setUpdatingStep(true);
            let result;

            // Apply appropriate transformer based on data type
            switch (state.selectedDataType?.id) {
                case ImportingDataTypes.partPriceLevels:
                    result = await transformerRegistry.partPriceLevel.run(parsedRows, isFinalRun, {mappingFields});
                    break;
                case ImportingDataTypes.partDescription:
                    result = await transformerRegistry.partDescription.run(parsedRows, isFinalRun);
                    break;
                case ImportingDataTypes.partTitle:
                    result = await transformerRegistry.partTitle.run(parsedRows, isFinalRun);
                    break;
                case ImportingDataTypes.partShortDescription:
                    result = await transformerRegistry.partShortDescription.run(parsedRows, isFinalRun);
                    break;
                case ImportingDataTypes.partNotes:
                    result = await transformerRegistry.partNotes.run(parsedRows, isFinalRun);
                    break;
                case ImportingDataTypes.partUPC:
                    result = await transformerRegistry.partUPC.run(parsedRows, isFinalRun);
                    break;
                case ImportingDataTypes.partVisibility:
                    result = await transformerRegistry.partVisibility.run(parsedRows, isFinalRun);
                    break;
                case ImportingDataTypes.partSerializability:
                    result = await transformerRegistry.partSerializability.run(parsedRows, isFinalRun);
                    break;
                case ImportingDataTypes.partDeliveryMode:
                    result = await transformerRegistry.partDeliveryMode.run(parsedRows, isFinalRun);
                    break;
                case ImportingDataTypes.partOnlineOrderEligibility:
                    result = await transformerRegistry.partOnlineOrderEligibility.run(parsedRows, isFinalRun);
                    break;
                case ImportingDataTypes.productVisibilityToOtherPartners:
                    result = await transformerRegistry.productVisibilityToOtherPartners.run(parsedRows, isFinalRun);
                    break;
                case ImportingDataTypes.customersTerritoriesAndAccountManagers:
                    result = await transformerRegistry.customersTerritoriesAndAccountManagers.run(parsedRows, isFinalRun);
                    break;
                case ImportingDataTypes.warehouseAlertThresholds:
                    result = await transformerRegistry.warehouseAlertThresholds.run(parsedRows, isFinalRun, allWarehouses);
                    break;
                case ImportingDataTypes.productDimensions:
                    result = await transformerRegistry.productDimensions.run(parsedRows, isFinalRun, {
                        transformLengthValue,
                        transformWeightValue,
                    });
                    break;
                case ImportingDataTypes.productVendors:
                    result = await transformerRegistry.productVendors.run(parsedRows, isFinalRun);
                    break;
                case ImportingDataTypes.priceMatrices:
                    result = await transformerRegistry.priceMatrices.run(parsedRows, isFinalRun, {
                        allPriceLevels,
                        allCategories,
                        allIndustries,
                    });
                    break;
                case ImportingDataTypes.externalCustomers:
                    result = await transformerRegistry.externalCustomers.run(parsedRows, isFinalRun);
                    break;
                case ImportingDataTypes.externalVendors:
                    result = await transformerRegistry.externalVendors.run(parsedRows, isFinalRun);
                    break;
                case ImportingDataTypes.productCategoryAssignment:
                    result = await transformerRegistry.productCategoryAssignment.run(parsedRows, isFinalRun);
                    break;
                case ImportingDataTypes.productsFromAnotherPartner:
                    result = await transformerRegistry.productsFromAnotherPartner.run(
                        parsedRows,
                        isFinalRun,
                        approvedPartners,
                        allWarehouses,
                    );
                    break;
                case ImportingDataTypes.productWarehouseAssignment:
                    result = await transformerRegistry.productWarehouseAssignment.run(
                        parsedRows,
                        isFinalRun,
                        allWarehouses,
                    );
                    break;
                case ImportingDataTypes.setInventoryAvailability:
                    result = await transformerRegistry.setInventoryAvailability.run(
                        parsedRows,
                        isFinalRun,
                        {
                            warehouses: allWarehouses,
                            serialImportSplitter: state.serialImportSplitter,
                        },
                    );
                    break;
                case ImportingDataTypes.productFormAssignment:
                    result = await transformerRegistry.productFormAssignment.run(parsedRows, isFinalRun, {
                        allForms,
                        formsApi,
                    });
                    break;
                case ImportingDataTypes.productRelationAssignment:
                    result = await transformerRegistry.productRelationAssignment.run(parsedRows, isFinalRun, {
                        allProductRelationTypes,
                    });
                    break;
                case ImportingDataTypes.productCodeAssignment:
                    result = await transformerRegistry.productCodeAssignment.run(parsedRows, isFinalRun, {
                        mappingFields,
                        codeImportSplitter: state.codeImportSplitter,
                        partnerId: window.partner?.id,
                    });
                    break;
                case ImportingDataTypes.partTechnicalInformation: {
                    const industryId = importIndustryId;
                    const properties = technicalPropertiesOfIndustry[industryId];
                    result = await transformerRegistry.partTechnicalInformation.run(
                        parsedRows,
                        isFinalRun,
                        industryId,
                        properties,
                        {mappingFields},
                    );
                    break;
                }
                case ImportingDataTypes.productProperties:
                    result = await transformerRegistry.productProperties.run(parsedRows, isFinalRun, importIndustryId);
                    break;
                case ImportingDataTypes.productPropertyOptions: {
                    const industryId = importIndustryId;
                    const properties = technicalPropertiesOfIndustry[industryId];
                    result = await transformerRegistry.productPropertyOptions.run(
                        parsedRows,
                        isFinalRun,
                        industryId,
                        properties,
                    );
                    break;
                }
                case ImportingDataTypes.externalProducts:
                    result = await transformerRegistry.externalProducts.run(parsedRows, isFinalRun, importIndustryId);
                    break;
                case ImportingDataTypes.productTagAssignment:
                    result = await transformerRegistry.productTagAssignment.run(parsedRows, isFinalRun, importIndustryId);
                    break;
                case ImportingDataTypes.removeProduct:
                    result = await transformerRegistry.removeProduct.run(parsedRows, isFinalRun);
                    break;
                case ImportingDataTypes.productFlatShippingRates:
                    result = await transformerRegistry.productFlatShippingRates.run(parsedRows, isFinalRun);
                    break;
                default:
                    result = parsedRows;
                    break;
            }

            // Handle transformation errors
            if (result?.error) {
                ToastService.warning({title: "Warning", message: result.error});
                return null;
            }

            // Standardize return type to { apiRequests, preventativeIssues }
            if (result && !result.apiRequests) {
                return {
                    apiRequests: result,
                    preventativeIssues: []
                };
            }

            return result;
        } catch (error) {
            console.error('Error transforming data:', error);
            ToastService.error({title: "Error", message: Texts.somethingWentWrong});
            return null;
        } finally {
            BaseImportTransformer.sharedImportTransformContext.clearSkippedFieldsByRow();
            setUpdatingStep(false);
        }
    }, [allPriceLevels, allWarehouses, approvedPartners, allForms, allProductRelationTypes, formsApi, state.selectedDataType?.id, state.userSkippedFieldCells, state.mappingFields, state.codeImportSplitter, state.serialImportSplitter, importIndustryId, technicalPropertiesOfIndustry, transformLengthValue, transformWeightValue, allCategories, allIndustries]);

    /**
     * Handles completion of data mapping
     * Updates state with transformed data and mapping status
     *
     * @param {Object} data - Object containing parsed rows and column mapping
     * @param {Array} data.parsedRows - The parsed data rows
     * @param {Array} data.fields - The fields to be used for mapping
     * @param {Object} data.columnMapping - Mapping of field names to CSV column titles
     */
    const onMappingComplete = useCallback(async ({parsedRows, fields, columnMapping}) => {
        const sessionMappingFields = buildMappingFieldsForSession(fields, columnMapping);
        const result = await transformImportedData(parsedRows, false, null, sessionMappingFields);
        if (!result) return;

        const importedFile = buildImportedFileFromParsedRows(parsedRows, fields, columnMapping);

        setState(prevState => ({
            ...prevState,
            originalApiRequests: result.apiRequests,
            apiRequests: result.apiRequests,
            preventativeIssues: result.preventativeIssues,
            columnMapping: columnMapping,
            importedFile: importedFile,
            parsedRows: parsedRows, // Store raw rows for corrections
            mappingFields: sessionMappingFields,
            propertyOptionsByPropertyId: result.propertyOptionsByPropertyId ?? {},
            userSkippedFieldCells: [],
            obsoleteRowIndices: result.obsoleteRowIndices ?? [],
            userSkippedRowIndices: [],
            step: ImportingSteps.review,
        }));
    }, [transformImportedData]);

    /**
     * Re-transforms data after user applies preventative corrections.
     * @param {Array<Object>} patchedParsedRows - The raw rows with applied patches
     */
    const applySkippedRowsToApiRequests = useCallback((apiRequests, skippedIndices) => {
        const skippedSet = new Set(skippedIndices ?? []);
        if (state.selectedDataType?.id === ImportingDataTypes.setInventoryAvailability) {
            return (apiRequests ?? [])
                .map((request) => {
                    if (!request?.rowIndices?.length) {
                        return request;
                    }
                    const kept = request.rowIndices
                        .map((rowIndex, itemIndex) => ({
                            rowIndex,
                            item: request.items?.[itemIndex],
                        }))
                        .filter(({rowIndex}) => !skippedSet.has(rowIndex));
                    if (!kept.length) {
                        return null;
                    }
                    return {
                        ...request,
                        items: kept.map((entry) => entry.item),
                        rowIndices: kept.map((entry) => entry.rowIndex),
                    };
                })
                .filter(Boolean);
        }
        return (apiRequests ?? []).map((request, index) => (skippedSet.has(index) ? null : request));
    }, [state.selectedDataType?.id]);

    /**
     * Handles changes to user-skipped row indices in preventative correction
     * @type {(function(*): void)|*}
     */
    const onUserSkippedRowIndicesChange = useCallback((ignoredRowIndices) => {
        setState((prev) => ({
            ...prev,
            userSkippedRowIndices: [...ignoredRowIndices],
            apiRequests: applySkippedRowsToApiRequests(prev.originalApiRequests, ignoredRowIndices),
        }));
    }, [applySkippedRowsToApiRequests]);

    const applyTransformResultToState = useCallback((result, skippedFieldCells, ignoredRowIndices) => {
        if (!result) {
            return null;
        }
        const apiRequests = applySkippedRowsToApiRequests(result.apiRequests, ignoredRowIndices);
        return {
            originalApiRequests: apiRequests,
            apiRequests,
            preventativeIssues: result.preventativeIssues,
            propertyOptionsByPropertyId: result.propertyOptionsByPropertyId ?? {},
            obsoleteRowIndices: result.obsoleteRowIndices ?? [],
            userSkippedFieldCells: skippedFieldCells ?? [],
        };
    }, [applySkippedRowsToApiRequests]);

    /**
     * Handles submission of preventative corrections by the user
     * @type {(function({patchedRows: *, ignoredRowIndices?: [], skippedFieldCells?: []}): Promise<void>)|*}
     */
    const onPreventativeCorrectionSubmit = useCallback(async ({
                                                                  patchedRows: patchedParsedRows,
                                                                  ignoredRowIndices = [],
                                                                  skippedFieldCells = [],
                                                              }) => {
        const result = await transformImportedData(patchedParsedRows, false, skippedFieldCells);
        if (!result) return;

        const transformPatch = applyTransformResultToState(result, skippedFieldCells, ignoredRowIndices);
        if (!transformPatch) return;

        const importedFile = buildImportedFileFromParsedRows(
            patchedParsedRows,
            state.mappingFields,
            state.columnMapping,
        );

        setState((prev) => ({
            ...prev,
            ...transformPatch,
            parsedRows: patchedParsedRows,
            importedFile: importedFile,
            propertyOptionsByPropertyId:
                transformPatch.propertyOptionsByPropertyId ?? prev.propertyOptionsByPropertyId,
            userSkippedRowIndices: [...ignoredRowIndices],
        }));
    }, [state.mappingFields, state.columnMapping, transformImportedData, applyTransformResultToState]);

    /**
     * Determines if API calls for a data type should be batched
     * @type {function(*): boolean}
     */
    const isDataTypeApiCallsBatched = useCallback((id) => {
        return [
            // TODO: add more data types here
            ImportingDataTypes.partVisibility,
            ImportingDataTypes.customersTerritoriesAndAccountManagers,
            ImportingDataTypes.productDimensions,
            ImportingDataTypes.setInventoryAvailability,
        ].includes(id);
    }, []);

    /**
     * Fetches records for batched API calls
     * @type {(function(*, *): *)}
     */
    const getBatchedApiRequestRecords = useCallback((selectedDataType, apiRequest) => {
        switch (selectedDataType?.id) {
            // TODO: add more data types here
            case ImportingDataTypes.partVisibility:
                return apiRequest?.models ?? [];
            case ImportingDataTypes.customersTerritoriesAndAccountManagers:
                return apiRequest?.dataList ?? [];
            case ImportingDataTypes.productDimensions:
                return apiRequest?.sizes ?? [];
            case ImportingDataTypes.setInventoryAvailability:
                return apiRequest?.items ?? [];
            default:
                return [];
        }
    }, []);

    const onFinalize = useCallback(async () => {
        const finalResult = await transformImportedData(
            state.parsedRows,
            true,
            state.userSkippedFieldCells,
        );
        if (finalResult) {
            const transformPatch = applyTransformResultToState(
                finalResult,
                state.userSkippedFieldCells,
                state.userSkippedRowIndices,
            );
            if (!transformPatch) {
                return false;
            }
            setState((prev) => ({
                ...prev,
                ...transformPatch,
                propertyOptionsByPropertyId:
                    transformPatch.propertyOptionsByPropertyId ?? prev.propertyOptionsByPropertyId,
            }));
            return true;
        }
        return false;
    }, [
        state.parsedRows,
        state.userSkippedRowIndices,
        state.userSkippedFieldCells,
        transformImportedData,
        applyTransformResultToState,
    ]);

    const userSkippedRowIndexSet = useMemo(() => new Set(state.userSkippedRowIndices ?? []), [state.userSkippedRowIndices],);

    /** Rows that will call the API (review + report summary). */
    const usableRecords = useMemo(() => {
        const skipped = userSkippedRowIndexSet;
        if (state.selectedDataType?.id === ImportingDataTypes.setInventoryAvailability) {
            return (state.apiRequests ?? []).reduce((sum, request) => {
                if (!request?.rowIndices?.length) {
                    return sum;
                }
                const activeRows = request.rowIndices.filter((rowIndex) => !skipped.has(rowIndex));
                return sum + activeRows.length;
            }, 0);
        }
        return (
            state.apiRequests?.filter((request, index) => request != null && !skipped.has(index))?.length ?? 0
        );
    }, [state.apiRequests, state.selectedDataType?.id, userSkippedRowIndexSet]);

    /**
     * Upper bound for import progress and grid row processing — every file row gets a report entry,
     * including skipped / no-op rows with a null apiRequest.
     */
    const importProgressTotal = useMemo(() => {
        return Math.max(0, state.importedFile?.length ?? state.apiRequests?.length ?? 0);
    }, [state.importedFile?.length, state.apiRequests?.length]);

    const skippedFieldLabelByKey = useMemo(() => {
        const labels = {};
        for (const field of state.mappingFields ?? []) {
            labels[field.name] = field.title;
        }
        return labels;
    }, [state.mappingFields]);

    const obsoleteRowIndexSet = useMemo(() => new Set(state.obsoleteRowIndices ?? []), [state.obsoleteRowIndices]);

    return (
        <>
            <div className={'importing-view'}>
                <Paper
                    className={'paper d-flex flex-column justify-content-center align-items-center'}
                    style={{borderRadius: '5px 5px 0 0'}}
                >
                    {/* Header Section */}
                    <Box className='d-flex flex-row justify-content-between align-items-start my-4 mt-3' sx={{width: '100%'}}>
                        <div className={'flex-grow-1'}>
                            <h2 className={'text-lg font-weight-semi-bold m-0 ml-3'}>
                                {t('pageTitle')}
                            </h2>
                            {
                                state.step === ImportingSteps.selectDataSource &&
                                <p className={'ml-3 mt-2 text-sm'}>
                                    {t('pageSubtitle')}
                                </p>
                            }
                            {
                                state.step !== ImportingSteps.selectDataSource &&
                                (
                                    state.selectedDataType?.id === ImportingDataTypes.partPriceLevels ||
                                    state.selectedDataType?.id === ImportingDataTypes.partTechnicalInformation
                                ) &&
                                <Typography variant="body1" color={'tertiary.main'} sx={{fontWeight: 'bold', textAlign: 'center', width: '100%', marginTop: 2}}>
                                    {t('leaveEmptyWarning')}
                                </Typography>
                            }
                            {
                                showMultiselectTechnicalImportWarning &&
                                <Typography variant="body1" color={'warning.main'} sx={{fontWeight: 'bold', textAlign: 'center', width: '100%', marginTop: 1}}>
                                    {t('dataSourceCatalog.alerts.multiselectNotSupported')}
                                </Typography>
                            }
                            {
                                state.selectedDataType?.id === ImportingDataTypes.setInventoryAvailability &&
                                <Box sx={{width: '100%', pt: 2, px: 3,}}>
                                    <Alert severity="warning" sx={{borderRadius: 1, width: '100%'}}>
                                        {t('dataSourceCatalog.alerts.setInventoryAvailabilityDanger')}
                                    </Alert>
                                </Box>
                            }
                        </div>
                        {/* New Import Button - Only shown after completion */}
                        {state.step === ImportingSteps.importing && processStatus === ImportingProcessStatus.finished && (
                            <Button
                                className='d-flex justify-content-center align-items-center mr-3 flex-shrink-0'
                                variant={'outlined'}
                                color={'primary'}
                                onClick={onNewProcessClicked}
                            >
                                <p className={'button-label'}>Import new data</p>
                            </Button>
                        )}
                    </Box>

                    {/* Stepper Navigation */}
                    <ImportingViewStepperSection
                        state={state}
                        setStep={setStep}
                        updatingStep={updatingStep}
                    />
                    {/* Main Content Area */}
                    {
                        (
                            state.step === ImportingSteps.selectDataSource ||
                            state.step === ImportingSteps.mapColumns
                        ) &&
                        <SelectImportDataSourceSection
                            state={state}
                            setState={setState}
                            onMappingComplete={onMappingComplete}
                            loading={staticDataLoading}
                            allPriceLevels={allPriceLevels}
                            allIndustries={allIndustries}
                            allWarehouses={allWarehouses}
                            technicalPropertiesOfIndustry={technicalPropertiesOfIndustry}
                            allPartCodeCategories={allPartCodeCategories}
                        />
                    }
                    {
                        state.step === ImportingSteps.review &&
                        <ReviewImportingSection
                            state={state}
                            usableRecords={usableRecords}
                            onPreventativeCorrectionSubmit={onPreventativeCorrectionSubmit}
                            onUserSkippedRowIndicesChange={onUserSkippedRowIndicesChange}
                            mappingFields={state.mappingFields}
                            staticDataContext={staticDataContext}
                        />
                    }
                    {
                        state.step === ImportingSteps.importing &&
                        <ImportingSection
                            state={state}
                            processStatus={processStatus}
                            setProcessStats={setProcessStats}
                            isDataTypeApiCallsBatched={isDataTypeApiCallsBatched}
                            getBatchedApiRequestRecords={getBatchedApiRequestRecords}
                            usableRecords={usableRecords}
                            importProgressTotal={importProgressTotal}
                            originalApiRequests={state.originalApiRequests}
                            interactiveSections={correctiveInteractions}
                            interactiveCorrectionEnabled={interactiveCorrectionEnabled}
                            setInteractiveCorrectionEnabled={handleInteractiveCorrectionEnabledChange}
                            setApiRequests={setApiRequests}
                            staticDataContext={staticDataContext}
                            userSkippedRowIndices={state.userSkippedRowIndices}
                            userSkippedFieldCells={state.userSkippedFieldCells}
                            obsoleteRowIndexSet={obsoleteRowIndexSet}
                            skippedFieldLabelByKey={skippedFieldLabelByKey}
                            serialImportSplitter={state.serialImportSplitter}
                        />
                    }
                </Paper>

                {/* Bottom Action Bar */}
                <ImportingBottomActionBar
                    state={state}
                    setStep={setStep}
                    onFinalize={onFinalize}
                    updatingStep={updatingStep}
                    showInteractiveCorrectionToggle={!!correctiveInteractions.length}
                    interactiveCorrectionEnabled={interactiveCorrectionEnabled}
                    setInteractiveCorrectionEnabled={handleInteractiveCorrectionEnabledChange}
                />
            </div>

            {/* Route Protection During Import */}
            <RouteLeavingGuard
                when={state.step === ImportingSteps.importing && (processStatus === ImportingProcessStatus.loading || processStatus === ImportingProcessStatus.paused)}
                shouldBlockNavigation={() => state.step === ImportingSteps.importing && (processStatus === ImportingProcessStatus.loading || processStatus === ImportingProcessStatus.paused)}
                navigate={(location) => history.replace(location)}
                confirmationMessage={"By leaving this page, the import process will be stopped and you will not have access to the report of the importing process later."}
                confirmationTitle={'Warning'}
            />
        </>
    );
};

export default ImportDataView;
